import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Ticket, Plus, X, UploadCloud, Send, Download, MessageSquare,
} from "lucide-react";

const statusBadge = (s) => ({
  "Open": "badge-blue", "In Progress": "badge-yellow",
  "Resolved": "badge-green", "Closed": "badge-gray",
}[s] || "badge-gray");

export default function Tickets() {
  const { user } = useAuth();
  const isConnector = user?.role === "connector";
  const [tickets, setTickets] = useState([]);
  const [meta, setMeta] = useState({ categories: [], statuses: [] });
  const [employees, setEmployees] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ category: "", subject: "", description: "" });
  const [file, setFile] = useState(null);
  const [updateAction, setUpdateAction] = useState({ status: "", admin_reply: "", assigned_to_id: "" });

  const load = async () => {
    const [t, m] = await Promise.all([api.get("/tickets"), api.get("/tickets/meta")]);
    setTickets(t.data);
    setMeta(m.data);
    if (!isConnector) {
      const emps = await api.get("/employees").catch(() => ({ data: [] }));
      setEmployees(emps.data);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const active = tickets.find((t) => t.id === activeId);

  const create = async (e) => {
    e.preventDefault();
    const r = await api.post("/tickets", form);
    if (file) {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/tickets/${r.data.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    }
    setForm({ category: "", subject: "", description: "" }); setFile(null); setCreating(false);
    await load(); setActiveId(r.data.id);
  };

  const openTicket = (t) => {
    setActiveId(t.id);
    setUpdateAction({ status: t.status, admin_reply: t.admin_reply || "", assigned_to_id: t.assigned_to_id || "" });
  };

  const applyUpdate = async () => {
    await api.patch(`/tickets/${activeId}`, updateAction);
    await load();
  };

  const uploadAttachment = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    await api.post(`/tickets/${activeId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    await load();
  };

  const downloadAttachment = async (a) => {
    const token = localStorage.getItem("crm_token");
    const res = await fetch(`${API_BASE}${a.url.replace("/api", "")}`, {
      headers: { Authorization: `Bearer ${token}` }, credentials: "include",
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a"); link.href = url; link.download = a.filename; link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold flex items-center gap-2"><Ticket size={26} /> Support Tickets</h1>
          <p className="text-sm text-muted-foreground">{isConnector ? "Raise issues and track resolution" : "Manage support requests from connectors"}</p>
        </div>
        {isConnector && (
          <button className="btn btn-primary" onClick={() => setCreating(true)} data-testid="btn-new-ticket"><Plus size={16} /> New Ticket</button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card overflow-hidden lg:col-span-1">
          <div className="p-3 border-b border-border font-semibold">All Tickets</div>
          <div className="max-h-[65vh] overflow-y-auto" data-testid="tickets-list">
            {tickets.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No tickets yet</div>}
            {tickets.map((t) => (
              <button key={t.id} onClick={() => openTicket(t)}
                className={`w-full text-left p-3 border-b border-border hover:bg-muted/50 ${activeId === t.id ? "bg-primary/5" : ""}`}
                data-testid={`ticket-item-${t.ticket_number}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono font-semibold text-sm">{t.ticket_number}</span>
                  <span className={`badge ${statusBadge(t.status)}`}>{t.status}</span>
                </div>
                <div className="text-sm font-semibold truncate mt-1">{t.subject}</div>
                <div className="text-xs text-muted-foreground truncate">{t.category} · {t.created_by_name}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="card overflow-hidden lg:col-span-2">
          {!active ? (
            <div className="p-12 text-center text-muted-foreground">Select a ticket to view details</div>
          ) : (
            <div className="p-5 space-y-4" data-testid="ticket-detail">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <div className="text-xs text-muted-foreground font-mono">{active.ticket_number}</div>
                  <h2 className="font-heading font-bold text-xl">{active.subject}</h2>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="badge badge-blue">{active.category}</span>
                    <span className={`badge ${statusBadge(active.status)}`}>{active.status}</span>
                    {active.assigned_to_name && <span className="badge badge-green">→ {active.assigned_to_name}</span>}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  <div>By: <strong>{active.created_by_name}</strong> ({active.created_by_username})</div>
                  <div>Phone: {active.created_by_phone || "—"} · Email: {active.created_by_email || "—"}</div>
                  <div>{new Date(active.created_at).toLocaleString()}</div>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 text-sm whitespace-pre-wrap">{active.description}</div>

              {active.attachments?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Attachments</div>
                  <div className="flex flex-wrap gap-2">
                    {active.attachments.map((a) => (
                      <button key={a.id} onClick={() => downloadAttachment(a)} className="btn btn-outline !py-1.5 !text-xs" data-testid={`att-${a.id}`}>
                        <Download size={12} /> {a.filename} ({(a.size / 1024).toFixed(1)}KB)
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {(isConnector || user?.role === "admin" || user?.role === "employee") && (
                <div>
                  <label className="btn btn-outline !py-1.5 !text-xs cursor-pointer" data-testid="ticket-upload-btn">
                    <UploadCloud size={14} /> Attach File
                    <input type="file" className="hidden" onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; uploadAttachment(f); }} />
                  </label>
                </div>
              )}

              {active.admin_reply && (
                <div className="border-l-4 border-primary bg-accent/50 rounded-md p-3">
                  <div className="text-[10px] uppercase font-semibold text-primary flex items-center gap-1"><MessageSquare size={10} /> Reply from Support</div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">{active.admin_reply}</div>
                </div>
              )}

              {!isConnector && (
                <div className="card !border-primary/30 p-4 space-y-3" data-testid="ticket-admin-panel">
                  <div className="font-semibold text-sm">Update Ticket</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground">Status</label>
                      <select className="input mt-1" value={updateAction.status} onChange={(e) => setUpdateAction({ ...updateAction, status: e.target.value })} data-testid="ticket-status-select">
                        {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    {user?.role === "admin" && (
                      <div>
                        <label className="text-xs font-semibold text-muted-foreground">Assign to</label>
                        <select className="input mt-1" value={updateAction.assigned_to_id} onChange={(e) => setUpdateAction({ ...updateAction, assigned_to_id: e.target.value })} data-testid="ticket-assign-select">
                          <option value="">Unassigned</option>
                          {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground">Reply</label>
                    <textarea className="input mt-1 min-h-[80px]" value={updateAction.admin_reply} onChange={(e) => setUpdateAction({ ...updateAction, admin_reply: e.target.value })} data-testid="ticket-reply" />
                  </div>
                  <button className="btn btn-primary" onClick={applyUpdate} data-testid="ticket-save-btn"><Send size={14} /> Save Update</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="card p-6 w-full max-w-lg fade-in" onClick={(e) => e.stopPropagation()} data-testid="ticket-create-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">New Support Ticket</h3>
              <button className="btn btn-ghost !p-1.5" onClick={() => setCreating(false)}><X size={16} /></button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Category *</label>
                <select required className="input mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="new-ticket-category">
                  <option value="">Choose…</option>
                  {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Subject *</label>
                <input required className="input mt-1" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="new-ticket-subject" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Description *</label>
                <textarea required className="input mt-1 min-h-[120px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-ticket-description" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Attachment (optional)</label>
                <label className="dropzone block mt-1 cursor-pointer">
                  <UploadCloud className="mx-auto mb-1 text-primary" size={20} />
                  <div className="text-sm">{file ? <span className="text-emerald-600">✓ {file.name}</span> : "Click to attach"}</div>
                  <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-outline" onClick={() => setCreating(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="new-ticket-submit">Create Ticket</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
